import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { NotificationService, AppNotification } from 'src/app/shared/services/notification.service';
import { FirestoreService } from 'src/app/shared/services/firestore.service';
import { Router } from '@angular/router';
import { Auth, onAuthStateChanged, Unsubscribe } from '@angular/fire/auth';
import { PageEvent } from '@angular/material/paginator';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss']
})
export class UserNotificationsComponent implements OnInit, OnDestroy {
  notifications: AppNotification[] = [];
  filteredNotifications: AppNotification[] = [];
  displayedColumns: string[] = ['status', 'type', 'title', 'message', 'createdAt'];
  
  // Paging
  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25, 50];

  searchTerm = '';
  currentUserId: string = '';
  private sub: Subscription | null = null;
  private authSub: Unsubscribe | null = null;

  // Filters Popup State
  showFilterPanel = false;
  statusFilter: 'All' | 'Read' | 'Unread' = 'All';
  typeFilter: 'All' | 'info' | 'success' | 'warning' | 'error' = 'All';
  dateFilter: 'All' | 'Today' | 'Last 7 Days' | 'Last 30 Days' = 'All';

  types = ['info', 'success', 'warning', 'error'];
  dateRanges = ['Today', 'Last 7 Days', 'Last 30 Days'];

  constructor(
    private notificationService: NotificationService,
    private firestoreService: FirestoreService,
    private router: Router,
    private auth: Auth
  ) {}

  @HostListener('document:click', ['$event'])
  clickout(event: Event) {
    if (this.showFilterPanel) {
      const filterWrapper = document.querySelector('.filter-wrapper');
      if (filterWrapper && !filterWrapper.contains(event.target as Node)) {
        this.showFilterPanel = false;
      }
    }
  }

  ngOnInit() {
    this.authSub = onAuthStateChanged(this.auth, (user) => {
      if (user) {
        this.currentUserId = user.uid;
        if (!this.sub) {
          this.sub = this.notificationService.notifications$.subscribe(notes => {
            this.notifications = notes;
            this.applyFilters();
          });
        }
      } else {
        this.currentUserId = '';
        this.notifications = [];
        this.filteredNotifications = [];
        if (this.sub) {
          this.sub.unsubscribe();
          this.sub = null;
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
    if (this.authSub) this.authSub();
  }

  onSearchChange(event: any) {
    this.searchTerm = event.target.value.toLowerCase();
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.notifications];

    if (this.searchTerm) {
      filtered = filtered.filter(n => 
        n.title.toLowerCase().includes(this.searchTerm) || 
        n.message.toLowerCase().includes(this.searchTerm)
      );
    }

    if (this.statusFilter !== 'All') {
      const targetRead = this.statusFilter === 'Read';
      filtered = filtered.filter(n => n.isRead === targetRead);
    }

    if (this.typeFilter !== 'All') {
      filtered = filtered.filter(n => n.type === this.typeFilter);
    }

    if (this.dateFilter !== 'All') {
      const now = new Date();
      let cutoff = new Date();
      if (this.dateFilter === 'Today') {
        cutoff.setHours(0, 0, 0, 0);
      } else if (this.dateFilter === 'Last 7 Days') {
        cutoff.setDate(now.getDate() - 7);
      } else if (this.dateFilter === 'Last 30 Days') {
        cutoff.setDate(now.getDate() - 30);
      }
      
      filtered = filtered.filter(n => {
        const d = n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.createdAt);
        return d >= cutoff;
      });
    }

    this.totalItems = filtered.length;
    
    // Pagination
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.filteredNotifications = filtered.slice(startIndex, endIndex);
  }

  handlePageEvent(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.applyFilters();
  }

  markAsRead(notification: AppNotification) {
    if (this.currentUserId && !notification.isRead) {
      this.notificationService.markAsRead(this.currentUserId, notification.id);
    }
  }

  clearAll() {
    if (this.currentUserId && confirm('Are you sure you want to clear all notifications?')) {
      this.notificationService.clearAllNotifications(this.currentUserId);
    }
  }

  handleNotificationClick(notification: AppNotification) {
    this.markAsRead(notification);
    const notifLink: string = notification.link || '';

    // Use the notification's embedded link to determine the correct destination.
    // '/business/...' → vendor booking-details flow
    // '/booking-orders' → customer order flow (even for vendors who booked as customers)
    if (notifLink.startsWith('/business/')) {
      if (notification.bookingId) {
        this.router.navigate(['/business/booking/booking-details', notification.bookingId]);
      } else {
        this.router.navigate(['/business/booking']);
      }
    } else if (notification.bookingId) {
      this.router.navigate(['/booking-orders', notification.bookingId]);
    } else {
      this.router.navigate(['/booking-orders']);
    }
  }

  setStatusFilter(status: any) {
    this.statusFilter = status;
    this.pageIndex = 0;
    this.applyFilters();
  }

  setTypeFilter(type: any) {
    this.typeFilter = type;
    this.pageIndex = 0;
    this.applyFilters();
  }

  setDateFilter(range: any) {
    this.dateFilter = range;
    this.pageIndex = 0;
    this.applyFilters();
  }

  clearAllFilters() {
    this.statusFilter = 'All';
    this.typeFilter = 'All';
    this.dateFilter = 'All';
    this.searchTerm = '';
    this.pageIndex = 0;
    this.applyFilters();
  }
}
